import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  InputAdornment,
  Tooltip,
  useTheme,
  CircularProgress,
  Button,
  Stack,
  Divider,
  InputLabel
} from '@mui/material';
import TextField from '../../components/TextField';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetApmQuery } from '../../services/appSlice/appSlice';
import TextFieldComponent from '../../components/TextField';
import { useUpdateSepMutation, useUpdateSepRequestorMutation } from '../../services/sepSlice';
import { useUpdateTaskMutation } from '../../services/tasksSlice/tasksSlice';
import { useGetSepTasksQuery } from '../../services/sepSlice'
import { User } from '../../../shared/types/User';
import { SEPPhase } from '../../../shared/types/SEP';
import { useUpdateDataFieldsMutation } from '../../services/dataFieldsSlice';
import { showSnackbar } from '../../../frontend/utils/snackbar';
import { useDispatch } from 'react-redux/es/hooks/useDispatch';
import { SEP_LIMIT_ERROR_MESSAGE, SEP_LIMIT_MAX_CHARACTERS, SEP_LIMIT_ALERT_DURATION } from '../../../frontend/utils/sharedConstants';
import UserSearchBar from '../../../frontend/components/TaskOverview/AutoComplete';

const emailFields = [
  {
    key: "itkeyManagerEmail",
    label: "IT Key Manager Email",
    placeholder: "Ex. it.architecture@constellation.com",
    refName: "ITKeyManagerEmailRef",
    editFlag: "isITKeyManagerEmailEdit",
    type: "itkeyManagerEmail",
  },
  {
    key: "vpEmail",
    label: "VP Email",
    placeholder: "Ex. vp.architecture@constellation.com",
    refName: "VPEmailRef",
    editFlag: "isVPEmailEdit",
    type: "vpEmail",
  },
];

const textFieldSx = {
  color: "#222222",
  "& > p": {
    margin: "5px 0px",
    color: "#646669",
    fontSize: typographyTokens.fontSizes.xs,
  },
};

const EditSEP = ({ sep, dataFields, onClose, isAdminUser }: { sep: any; dataFields: any; onClose: () => void; isAdminUser: boolean }) => {
  const altContactDataField = dataFields?.find((item: any) => {
    return item.name == 'Alternate Contact'
  })
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [updateDataFields] = useUpdateDataFieldsMutation();
  const [altContact, setAltContact] = useState(altContactDataField?.value);
  const [updateSep] = useUpdateSepMutation();
  const [updateSepRequestor] = useUpdateSepRequestorMutation();
  const requestorRef = useRef(null);
  const altContactRef = useRef(null);
  const [openRequestorEdit, setOpenRequestorEdit] = useState(false);
  const [openAltContactEdit, setOpenAltContactEdit] = useState(false);
  const [newRequestor, setNewRequestor] = useState<User | null>(null);
  const [sepName, setSepName] = useState<string>('');
  const [sepDesc, setSepDesc] = useState<string>('');
  const [apmID, setApmID] = useState<string>('');
  const [searchField, setSearchField] = useState<number | null | string>(null);
  const [activelyTyping, setActivelyTyping] = useState<number | null | string>(
    null
  );
  const [errorMsg, setErrorMsg] = useState('');
  const { data, isError, isLoading, error, refetch } = useGetApmQuery(
    searchField || skipToken
  );
  const { data: sepTasks } = useGetSepTasksQuery(sep.id);
  const [updateTaskMutation] = useUpdateTaskMutation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const dispatch = useDispatch();

  const refs = {
    ITKeyManagerEmailRef: useRef(null),
    VPEmailRef: useRef(null),
  };

  const [form, setForm] = useState({
    vpEmail: '',
    itkeyManagerEmail: '',
  });

  const [editFlags, setEditFlags] = useState({
    isITKeyManagerEmailEdit: false,
    isVPEmailEdit: false,
  });

  const openEdit = (editFlag: string) => {
    setEditFlags({
      isITKeyManagerEmailEdit: false,
      isVPEmailEdit: false,
      [editFlag]: true,
    });
  };

  const closeEdit = () => {
    setEditFlags({
      isITKeyManagerEmailEdit: false,
      isVPEmailEdit: false,
    });
  };

  const handleChangeUser = (type: string, user: any) => {
    const field = emailFields.find((f) => f.type === type);
    if (field) {
      setForm((prev) => ({
        ...prev,
        [field.key]: user?.email || "",
      }));
    }
    closeEdit();
  };

  const handleChangeRequestor = (type: string, user: User) => {
    setNewRequestor(user);
    setOpenRequestorEdit(false);
  };

  const handleChangeAltContact = (type: string, user: User) => {
    setAltContact(user?.email || null);
    setOpenAltContactEdit(false);
  };

  useEffect(() => {
    const dataFieldAltContact = dataFields?.find((item: any) => {
      return item.name == 'Alternate Contact'
    })
    setAltContact(dataFieldAltContact?.value)
  }, [dataFields])

  const onEnterPress = (e: any) => e.key === 'Enter' && handleFind();

  const handleChange = (e: any) => {
    setActivelyTyping(e.target.value);
    setApmID('');
  };


  const checkbvDrMisalignment = (apmdata: any) => {

    const highTierValues = ['Tier 0', 'Tier 1', 'Tier 2']
    const mismatch = apmdata?.emergency_tier && !((highTierValues.includes(apmdata?.emergency_tier) && apmdata?.business_criticality && apmdata?.business_criticality.toLowerCase() == 'high') ||
      (apmdata?.emergency_tier && apmdata?.emergency_tier == 'Tier 3' && apmdata?.business_criticality && apmdata?.business_criticality.toLowerCase() == 'medium') ||
      (apmdata?.emergency_tier && apmdata?.emergency_tier == 'Tier 4' && apmdata?.business_criticality && apmdata?.business_criticality.toLowerCase() == 'low'))
    return mismatch;
  }
  const handleSave = () => {
    if (data) {
      const isDataMisaligned = checkbvDrMisalignment(data)
      const bvDRTask = sepTasks && sepTasks.filter(
        (e: any) => e.taskTemplateID == 3040
      )[0];
      if (isDataMisaligned && bvDRTask) {
        updateTaskMutation({
          taskID: bvDRTask.id,
          task: {
            enabled: true,
            review: true,
          },
        });
      }
    }

    updateSep({
      id: sep?.id,
      body: {
        name: sepName,
        description: sepDesc,
        apmID: parseInt(apmID || ''),
        apmData: !isNaN(parseInt(apmID)) ? data : null,
        vpEmail: form?.vpEmail || '',
        itkeyManagerEmail: form?.itkeyManagerEmail || ''
      },
    });

    // Update Requestor if changed
    if (newRequestor) {
      const oldRequestorName = sep?.creator?.displayName || sep?.creator?.givenName || 'Previous Requestor';
      updateSepRequestor({
        id: sep?.id,
        body: {
          id: newRequestor.id,
          displayName: newRequestor.displayName,
          givenName: newRequestor.givenName,
          surname: newRequestor.surname,
          email: newRequestor.email,
        },
        oldRequestorName: oldRequestorName,
      } as any);
    }

    if (altContact !== altContactDataField.value) {
      const data = [{
        id: altContactDataField?.id,
        value: altContact
      }]
      updateDataFields({ sepId: sep?.id, dataFields: data });
    }
    onClose();
  };

  const handleFind = () => {
    if (activelyTyping !== null && activelyTyping !== '') {
      if (activelyTyping === data?.number) {
        setApmID(activelyTyping);
        setErrorMsg('');
      } else {
        setSearchField(activelyTyping);
      }
    }
  };

  const handleSEPDescChange = (e: string) => {
    setSepDesc(e);
  };

  useEffect(() => {
    if (sep) {
      setSepName(sep.name || '');
      setSepDesc(sep.description || '');
      setApmID(sep.apmID || '');
      setActivelyTyping(sep.apmID || '');
      setSearchField(sep.apmID || '');
      setForm({
        vpEmail: sep?.vpEmail || '',
        itkeyManagerEmail: sep?.itkeyManagerEmail || '',
      })
    }
  }, [sep]);

  useEffect(() => {
    if (data) {
      setApmID((e) => data.number || '');
      setErrorMsg('');
    }
    if (error && 'data' in error) {
      setApmID((e) => '');
      setErrorMsg(error.data as string);
    }
  }, [data, error]);


  const handleSEPNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length <= SEP_LIMIT_MAX_CHARACTERS) {
      setSepName(value);
    } else {
      setSepName(value.slice(0, SEP_LIMIT_MAX_CHARACTERS));
      showSnackbar(
        dispatch,
        { errorMessage: SEP_LIMIT_ERROR_MESSAGE },
        'error',
        SEP_LIMIT_ALERT_DURATION
      );
    }
  };

  return (
    <>
      <Box sx={{ px: 4, py: 3, width: '100%' }}>
        <Box>
          <TextFieldComponent
            name="name"
            onChange={(e: any) => handleSEPNameChange(e)}
            label="Initiative Name"
            caption="Give your initiative request a name"
            fullWidth
            value={sepName}
          />
          <Box mt={2} />
          <TextFieldComponent
            multiline
            name="description"
            onChange={(e: any) => handleSEPDescChange(e)}
            label="Description"
            caption="Please describe this initiative in as much detail as possible"
            rows={10}
            fullWidth
            value={sepDesc}
            isDomString
            readOnly={!isAdminUser && sep?.phase === SEPPhase.complete}
          />
        </Box>

        {/* Requestor Field */}
        <Box sx={{ mt: 3, mb: 2 }} ref={requestorRef}>
          <InputLabel>
            Requestor
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="medium"
            fullWidth
            value={
              newRequestor
                ? (newRequestor.givenName && newRequestor.surname
                  ? `${newRequestor.givenName} ${newRequestor.surname}`
                  : newRequestor.displayName || '')
                : (sep?.creator?.givenName && sep?.creator?.surname
                  ? `${sep?.creator?.givenName} ${sep?.creator?.surname}`
                  : sep?.creator?.displayName || '')
            }
            onClick={() => setOpenRequestorEdit(true)}
            placeholder="Click to change requestor"
            InputProps={{ readOnly: true }}
          />
          <UserSearchBar
            type="requestor"
            show={openRequestorEdit}
            anchor={requestorRef.current}
            showConfirmButton={true}
            onClose={() => setOpenRequestorEdit(false)}
            onChange={(user: User) => handleChangeRequestor('requestor', user)}
          />
        </Box>

        {/* Alternate Contact Field */}
        <Box sx={{ mt: 3, mb: 2 }} ref={altContactRef}>
          <InputLabel>
            Alternate Contact
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="medium"
            fullWidth
            value={altContact || ''}
            onClick={() => setOpenAltContactEdit(true)}
            placeholder="Click to select alternate contact"
            InputProps={{ readOnly: true }}
          />
          <UserSearchBar
            type="altContact"
            show={openAltContactEdit}
            anchor={altContactRef.current}
            showConfirmButton={true}
            onClose={() => setOpenAltContactEdit(false)}
            onChange={(user: User) => handleChangeAltContact('altContact', user)}
          />
          <Button sx={{ fontSize: typographyTokens.fontSizes.xxs }} onClick={() => setAltContact(null)}>Remove Alternate contact</Button>
        </Box>

        <Box sx={{ mt: 3, mb: 2 }}>
          <TextFieldComponent
            label="Requestor Department Name"
            caption="Requestor's Department Name"
            fullWidth
            value={sep?.requestorDepartment}
            disabled
          />
        </Box>
        {emailFields.map((field) => {
          const refKey = field.refName as keyof typeof refs;
          const formKey = field.key as keyof typeof form;
          const editFlagKey = field.editFlag as keyof typeof editFlags;

          return (
            <Box sx={{ mb: 2 }} ref={refs[refKey]} key={field.key}>
              <InputLabel>
                {field.label}
              </InputLabel>
              <TextField
                sx={textFieldSx}
                size="small"
                fullWidth
                value={form[formKey]}
                onClick={() => openEdit(field.editFlag)}
                placeholder={field.placeholder}
                name={field.key}
              />
              <UserSearchBar
                type={field.type}
                show={editFlags[editFlagKey]}
                anchor={refs[refKey].current}
                onClose={closeEdit}
                onChange={(user: any) => handleChangeUser(field.type, user)}
              />
            </Box>
          );
        })}

        <Grid container sx={{ width: '100%' }}>
          <Grid item xs={12} md={5}>
            {/* this is the error on console for being null */}
            <TextField
              label="APM ID"
              icon={
                <Tooltip placement="top" title="The APM ID from Service Now.">
                  <InfoIcon sx={{ color: theme.palette.gray.solidGray }} />
                </Tooltip>
              }
              caption="Click the search icon for details"
              type="number"
              error={isError || errorMsg !== ''}
              helperText={errorMsg}
              onKeyUp={(e: any) => onEnterPress(e)}
              endAdornment={
                <div onClick={() => handleFind()}>
                  <InputAdornment
                    sx={{ mr: 1, cursor: 'pointer' }}
                    position="end"
                  >
                    <SearchIcon />
                  </InputAdornment>
                </div>
              }
              onChange={(e: any) => handleChange(e)}
              value={activelyTyping || ''}
              sx={{ width: '100%', mr: 1 }}
            />
          </Grid>
          <Grid item xs={0} md={1}></Grid>
          <Grid item xs={12} md-offset={1} md={6}>
            <TextField
              disabled
              label="APM Name"
              sx={{ width: '100%' }}
              value={(apmID !== '' ? data?.name : '') || ''}
              endAdornment={
                isLoading && (
                  <InputAdornment
                    sx={{ mr: 1, cursor: 'pointer' }}
                    position="end"
                  >
                    <CircularProgress
                      size="20px"
                      sx={{ color: theme.palette.gray.solidGray }}
                    />
                  </InputAdornment>
                )
              }
            />
          </Grid>
        </Grid>
        <Button startIcon={<RefreshIcon />} onClick={() => { setSearchField(activelyTyping); refetch() }}>Refresh APM Details</Button>
      </Box>
      <Divider />
      <Box sx={{ px: 4, py: 2, width: '100%' }}>
        <Stack
          direction="row"
          display="flex"
          justifyContent="right"
          spacing={2}
        >
          <Button
            sx={{ color: theme.palette.prime.blue, textTransform: 'none' }}
            variant="text"
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            sx={{ backgroundColor: theme.palette.prime.blue, textTransform: 'none' }}
            variant="contained"
            onClick={() => handleSave()}
            disabled={
              sepName === '' ||
              (errorMsg !== '' && activelyTyping !== '') ||
              (!!!apmID && activelyTyping !== '')
            }
          >
            Save Changes
          </Button>
        </Stack>
      </Box>
    </>
  );
};

export default EditSEP;
